import jsQR from 'jsqr';
import { SyncPortError } from '../../shared/errors.js';

export type CameraFailure = 'denied' | 'not-found' | 'busy' | 'other';

export class CameraError extends SyncPortError {
  constructor(
    readonly failure: CameraFailure,
    readonly reason: string,
  ) {
    super(`Camera could not be opened (${failure}): ${reason}`);
  }
}

/** Classifies getUserMedia failures so the UI can explain them in the user's language. */
export const classifyCameraError = (error: unknown): CameraError => {
  if (error instanceof DOMException) {
    switch (error.name) {
      case 'NotAllowedError':
        return new CameraError('denied', error.name);
      case 'NotFoundError':
        return new CameraError('not-found', error.name);
      case 'NotReadableError':
        return new CameraError('busy', error.name);
      default:
        return new CameraError('other', error.name);
    }
  }
  return new CameraError('other', String(error));
};

export const hasCamera = (nav: Navigator): boolean => typeof nav.mediaDevices?.getUserMedia === 'function';

/** Prefers the rear camera on phones; "ideal" keeps laptops with a single front camera working. */
export const openRearCamera = async (): Promise<MediaStream> => {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    });
  } catch (error) {
    throw classifyCameraError(error);
  }
};

export const stopStream = (stream: MediaStream): void => {
  for (const track of stream.getTracks()) {
    track.stop();
  }
};

/** Decodes a QR code from the current video frame, or null when none is visible. */
export const decodeFrame = (video: HTMLVideoElement, canvas: HTMLCanvasElement): string | null => {
  if (video.readyState < video.HAVE_ENOUGH_DATA || video.videoWidth === 0) {
    return null;
  }
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) {
    throw new CameraError('other', 'Canvas 2D context is unavailable');
  }
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  const code = jsQR(image.data, image.width, image.height, { inversionAttempts: 'dontInvert' });
  return code === null || code.data === '' ? null : code.data;
};

export type ScanLoop = Readonly<{ stop: () => void }>;

/** Scans frames until the callback accepts one. Pure decoding lives in decodeFrame; this only schedules. */
export const startScanLoop = (video: HTMLVideoElement, onDecoded: (text: string) => boolean): ScanLoop => {
  const canvas = document.createElement('canvas');
  let active = true;
  const tick = (): void => {
    if (!active) {
      return;
    }
    const text = decodeFrame(video, canvas);
    if (text !== null && onDecoded(text)) {
      active = false;
      return;
    }
    window.requestAnimationFrame(tick);
  };
  window.requestAnimationFrame(tick);
  return { stop: () => {
    active = false;
  } };
};
